import { getAppConfigSingleton } from "@expert/app-config";
import { getLogger } from "@expert/logging";
import { setAppcuesIdentifier, setFullStoryIdentifier, trackAppcuesEvent } from "@expert/monitoring";
import { appcuesBaseAttributes } from "@expert/monitoring/src/appcues";
import { useGuideStore } from "@expert/solve-tools";
import { Flex } from "@mantine/core";
import { useWindowEvent } from "@mantine/hooks";
import { AnalyticsProvider } from "@soluto-private/eventualize-react";
import { useSetDraggableBounds } from "@soluto-private/expert-workspace-notepad";
import { useTimelineStore } from "@soluto-private/expert-workspace-timeline";
import { type JSX, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary, withErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";
import { useCallDetailsStore } from "@/call-details";
import { env } from "@/config";
import { useVerifiedCustomerStore } from "@/customer-info";
import { CustomerPanel, useVerificationFlowStore } from "@/customer-panel";
import { useCallIntroStore } from "@/customer-panel/components/call-intro-flow/store/CallIntroStore";
import { MockCustomerPanel } from "@/customer-panel/components/customer-panel/MockCustomerPanel";
import { ExpertUniversityHome } from "@/expert-university";
import { useTechCuesListener } from "@/gaia-client/hooks/useTechCuesListener";
import { NotificationContainer } from "@/notification/NotificationContainer";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { MicroLearning, useProductOfferStore } from "@/sales-tools";
import { Announcement } from "@/sales-tools/challenges/Announcement";
import { useChallengeEventListener } from "@/sales-tools/challenges/hooks/useChallengeEventListener";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useEnrollmentEventListener } from "@/sales-tools/home-product/hooks/useEnrollEventListener";
import { useIntegratedFlowStore } from "@/sales-tools/home-product/stores/useIntegratedFlowStore";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useRapportBuildingListener } from "@/sales-tools/rapport-building/useRapportBuildingListener";
import { useSalesChecklistEventListener } from "@/sales-tools/sales-checklist/useSalesChecklistEventListener";
import { WidgetContainer } from "@/sales-tools/widgets";
import {
    DevDebugTools,
    isSimulatedVoiceTask,
    isTrainingTask,
    sdkEventBus,
    useActiveTask,
    useAgentSdk,
    useAgentStore,
    useChatTasks,
    usePartner,
    useSession,
} from "@/sdk";
import { useSessionSummaryListener } from "@/session-summary/api/useSessionSummaryListener";
import { createFallbackRender, RenderError, useFeatureFlag, userCache } from "@/shared-utils";
import { Transcript } from "@/transcript/Transcript";
import { Troubleshooting, useTroubleshootingStore } from "@/troubleshoot";
import { ChatManager } from "../chat";
import { WorkspaceHeader } from "../header";
import { TrainingModal } from "../training";
import { WorkspaceBody } from "./WorkspaceBody";
import styles from "./workspace.module.css";

const logger = getLogger({ module: "Workspace" });

const TrainingModalHandled = withErrorBoundary(TrainingModal, { fallbackRender: createFallbackRender(logger) });
const MicroLearningModalHandled = withErrorBoundary(MicroLearning, { fallbackRender: createFallbackRender(logger) });
const AnnouncementModalHandled = withErrorBoundary(Announcement, { fallbackRender: createFallbackRender(logger) });
const TranscriptHandled = withErrorBoundary(Transcript, { FallbackComponent: RenderError });

export function Workspace({ isAuthRedirectNeededRef }: { isAuthRedirectNeededRef: RefObject<boolean> }): JSX.Element {
    const activeTask = useActiveTask();
    const navigate = useNavigate();
    const sdk = useAgentSdk();
    const { agentId, agentName, activity: currentActivity } = useAgentStore((state) => state);
    const { isTroubleshooting, setIsTroubleshooting } = useTroubleshootingStore();

    const chatTasks = useChatTasks();
    const { employeeId, userEmail, identity, givenName, familyName } = userCache;
    const session = useSession();
    const sessionId = session.id;

    const isCustomerSession = session.kind === "with-customer";
    const partner = usePartner();
    const activeTaskIdentities = activeTask ? { TaskId: activeTask.id } : {};
    const activeTaskExtra = activeTask
        ? {
              taskType: activeTask.type,
              partner: activeTask.partner,
          }
        : {};

    const { simplifiedSummary, customerPanelEnabled, logOutOnOfflineAgentActivity, isRealtimeTranscriptEnabled } =
        usePartnerFeatureConfigured();
    const { data: isMicroLearningV2Enabled } = useFeatureFlag("EWP-isMicroLearningV2Enabled", { partner });
    const { data: isChallengeEnabled = false } = useFeatureFlag("Sales-isChallengeEnabled", {
        partner,
    });

    const { data: isEnrolledInExpertUniversity } = useFeatureFlag("isEnrolledInExpertUniversity", undefined);

    const [hasCallEnded, setHasCallEnded] = useState(false);

    const activeTrainingTask = useMemo(() => {
        return isTrainingTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const showExpertUniversity = !activeTask && currentActivity === "Training" && isEnrolledInExpertUniversity;

    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    useTechCuesListener(partner);
    useSessionSummaryListener(simplifiedSummary);
    useSalesChecklistEventListener();
    useRapportBuildingListener();
    useChallengeEventListener();
    useEnrollmentEventListener();
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const agentOfflineEvent = sdkEventBus.on("agent_activity_changed", (activity) => {
            if (logOutOnOfflineAgentActivity && activity === sdk.offlineAgentActivity) {
                sdk.logout?.();
                localStorage.clear();
                sessionStorage.clear();
                void navigate("/logged-out");
            }
        });

        const technicalIssuesEvent = sdkEventBus.on("technical_issues", () => {
            logger.info("Troubleshooting | open");
            setIsTroubleshooting(true);
        });

        const sessionChangedEvent = sdkEventBus.on("session_changed", ({ action }) => {
            if (action === "end" && isMicroLearningV2Enabled) {
                logger.info("MicroLearning triggered");
                setHasCallEnded(true);
            }
        });
        return () => {
            agentOfflineEvent();
            technicalIssuesEvent();
            sessionChangedEvent();
        };
    }, [setIsTroubleshooting, navigate, sdk, isMicroLearningV2Enabled]);

    useWindowEvent("beforeunload", (ev) => {
        // show confirmation dialog while with a customer (e.g. on a call) unless we're redirecting to re-authenticate
        if (!isCustomerSession || isAuthRedirectNeededRef.current) return;
        ev.preventDefault();
        ev.returnValue = "";
        return "";
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        setFullStoryIdentifier({ identity: agentName, displayName: agentName, properties: { agentId, employeeId } });
        setAppcuesIdentifier({
            identity: employeeId ?? agentId,
            name: givenName && familyName ? `${givenName} ${familyName}` : agentName,
            email: userEmail ?? identity,
            partner,
        });

        trackAppcuesEvent("Workspace Loaded", { partner });
    }, []);

    useEffect(() => {
        appcuesBaseAttributes.partner = partner;

        // Check if partner config exists in the appConfigSingleton on partner change and warn once if not
        if (partner && partner !== "unknown") {
            const appConfig = getAppConfigSingleton();
            if (!appConfig.hasPartnerConfig(partner)) {
                logger.warn(`Partner configuration not found for '${partner}', 'unknown' configuration will be used.`);
            }
        }
    }, [partner]);

    useEffect(() => {
        if (env.channel === "chat") {
            useTimelineStore.getState().setUseDefaultTimelineKey(false);
        }
    }, []);

    useEffect(() => {
        // rehydrate stores if current session is with a customer and it has not ended
        if (session.metadata.status !== "ended" && session.kind === "with-customer") {
            void useVerificationFlowStore.persist.rehydrate();
            void useCallDetailsStore.persist.rehydrate();
            void useProductOfferStore.persist.rehydrate();
            void useVerifiedCustomerStore.persist.rehydrate();
            void useGuideStore.persist.rehydrate();
            void useOfferChecklistStore.persist.rehydrate();
            void useDetectedCuesStore.persist.rehydrate();
            void useIntegratedFlowStore.persist.rehydrate();
            void useCallIntroStore.persist.rehydrate();
        }
    }, [session]);

    const containerRef = useRef(null);
    useSetDraggableBounds(containerRef);

    return (
        <Flex ref={containerRef} className={styles.container}>
            {env.isProdMode ? null : <DevDebugTools />}

            <AnalyticsProvider
                dispatcher={(dispatcher) =>
                    dispatcher
                        .withIdentities({
                            WorkerId: agentId,
                            SessionId: sessionId,
                            ...activeTaskIdentities,
                        })
                        .withExtra({ partner, ...activeTaskExtra })
                }
            >
                <WorkspaceHeader />
                {env.channel === "chat" && <ChatManager chatTasks={chatTasks} />}
                {env.channel === "voice" && (
                    <>
                        {isRealtimeTranscriptEnabled && (isCustomerSession || activeExpertUniversityTask) ? (
                            <TranscriptHandled taskStartTimeInMs={session.startedTimestamp} />
                        ) : null}
                        {!isRealtimeTranscriptEnabled && !isEnrolledInExpertUniversity && <WidgetContainer />}
                        {showExpertUniversity ? (
                            // If the conditions are met for Expert University, show the Training home page
                            <ExpertUniversityHome />
                        ) : activeTrainingTask?.startTimestamp ? (
                            <Flex className={styles.workspaceBody} direction="row" m="2.5rem 3rem">
                                <TrainingModalHandled task={activeTrainingTask} />
                            </Flex>
                        ) : (
                            <WorkspaceBody />
                        )}
                        {/* This error boundary is swallowing up errors. We need to have a better way to show customer panel errors while still the panel */}
                        {isCustomerSession && customerPanelEnabled ? (
                            <Flex m="sm" ml={0} flex={1} miw="350px" maw="400px">
                                <ErrorBoundary fallback={<RenderError />}>
                                    <CustomerPanel />
                                </ErrorBoundary>
                            </Flex>
                        ) : null}
                        {activeExpertUniversityTask && (
                            <ErrorBoundary fallback={<RenderError />}>
                                <MockCustomerPanel />
                            </ErrorBoundary>
                        )}
                        {(isChallengeEnabled || isMicroLearningV2Enabled) &&
                        hasCallEnded &&
                        !isCustomerSession &&
                        currentActivity === sdk.availableAgentActivity ? (
                            <>
                                <AnnouncementModalHandled />
                                <MicroLearningModalHandled />
                            </>
                        ) : null}
                        {(isChallengeEnabled || isMicroLearningV2Enabled) &&
                            currentActivity === sdk.defaultAgentActivity && <AnnouncementModalHandled />}
                        {isTroubleshooting ? <Troubleshooting /> : null}
                        <NotificationContainer />
                    </>
                )}
            </AnalyticsProvider>
        </Flex>
    );
}
